import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input, Select, Textarea } from '@artosera/ui';
import { exceptionRdvCreationSchema } from '@artosera/shared';
import {
  priseRdvApi,
  useActionRdv,
  useExceptionsRdv,
  type ExceptionRdv,
  type TypeRdv,
} from '../../api/priseRendezVous.js';
import { useShellContext } from '../shell/context.js';
import { ErreurRdv } from './ErreurRdv.js';
import { dateHeureRdv, lireSaisieRdv, saisieLocaleRdv } from './temps.js';
import styles from './RendezVous.module.css';

type ActionException =
  | { geste: 'sauver'; id?: string; corps: Omit<ExceptionRdv, 'id'> }
  | { geste: 'supprimer'; id: string; version: number };
export function ExceptionsListe({ types, fuseau }: { types: TypeRdv[]; fuseau: string }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const liste = useExceptionsRdv(galerieSlug);
  const [edition, setEdition] = useState<string | null>(null);
  const exception = liste.data?.exceptions.find((e) => e.id === edition);
  const mutation = useActionRdv(galerieSlug, async (action: ActionException, appel) => {
    if (action.geste === 'supprimer') {
      await priseRdvApi.supprimerException(galerieSlug, action.id, action.version, appel);
      return;
    }
    if (action.id) await priseRdvApi.modifierException(galerieSlug, action.id, action.corps, appel);
    else {
      const { version: _version, ...corps } = action.corps;
      await priseRdvApi.creerException(galerieSlug, corps, appel);
    }
  });
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <section className={styles.bloc}>
      <div className={styles.entete}>
        <h2>{t('regie:priseRendezVous.exceptions')}</h2>
        <Button
          onClick={() => {
            mutation.reset();
            setEdition('nouvelle');
          }}
        >
          {t('regie:priseRendezVous.nouvelleException')}
        </Button>
      </div>
      <p>
        {fuseau} · {t('regie:priseRendezVous.exceptionsAide')}
      </p>
      <ErreurRdv erreur={liste.error ?? mutation.error} />
      {liste.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {edition !== null ? (
        <ExceptionFormulaire
          key={`${edition}:${exception?.version ?? 0}:${fuseau}`}
          exception={exception}
          types={types}
          fuseau={fuseau}
          pending={mutation.isPending}
          onSauver={(corps) =>
            void mutation
              .mutateAsync({ geste: 'sauver', id: exception?.id, corps })
              .then(() => setEdition(null))
              .catch(() => {})
          }
          onFermer={() => setEdition(null)}
        />
      ) : null}
      <ul className={styles.liste}>
        {liste.data?.exceptions.map((e) => (
          <li key={e.id} className={styles.carte}>
            <h3>{t(`regie:priseRendezVous.${e.sorte}`)}</h3>
            <p>
              {dateHeureRdv(e.du, fuseau)} – {dateHeureRdv(e.au, fuseau)}
            </p>
            <p>{e.note}</p>
            <p>
              {e.typeIds.length
                ? e.typeIds
                    .map((id) => {
                      const type = types.find((v) => v.id === id);
                      return type?.titre[langue] || type?.titre.fr || id;
                    })
                    .join(', ')
                : t('regie:priseRendezVous.tousTypes')}
            </p>
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={() => {
                  mutation.reset();
                  setEdition(e.id);
                }}
              >
                {t('commun:actions.modifier')}
              </Button>
              <Button
                variant="ghost"
                loading={mutation.isPending}
                onClick={() =>
                  mutation.mutate({ geste: 'supprimer', id: e.id, version: e.version })
                }
              >
                {t('commun:actions.supprimer')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
function ExceptionFormulaire({
  exception,
  types,
  fuseau,
  pending,
  onSauver,
  onFermer,
}: {
  exception?: ExceptionRdv;
  types: TypeRdv[];
  fuseau: string;
  pending: boolean;
  onSauver: (corps: Omit<ExceptionRdv, 'id'>) => void;
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [sorte, setSorte] = useState(exception?.sorte ?? 'fermeture');
  const [du, setDu] = useState(exception ? saisieLocaleRdv(exception.du, fuseau) : '');
  const [au, setAu] = useState(exception ? saisieLocaleRdv(exception.au, fuseau) : '');
  const [typeIds, setTypeIds] = useState(exception?.typeIds ?? []);
  const [note, setNote] = useState(exception?.note ?? '');
  const [invalide, setInvalide] = useState(false);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        const resultat = exceptionRdvCreationSchema.safeParse({
          sorte,
          du: lireSaisieRdv(du, fuseau),
          au: lireSaisieRdv(au, fuseau),
          typeIds,
          note,
        });
        setInvalide(!resultat.success);
        if (resultat.success) onSauver({ ...resultat.data, version: exception?.version ?? 0 });
      }}
    >
      <fieldset className={styles.bloc} disabled={pending}>
        <legend>{t('regie:priseRendezVous.exception')}</legend>
        <label className={styles.champ}>
          {t('regie:priseRendezVous.sorteException')}
          <Select
            value={sorte}
            onValueChange={(v) => setSorte(v as typeof sorte)}
            options={['fermeture', 'ouverture'].map((v) => ({
              value: v,
              label: t(`regie:priseRendezVous.${v}`),
            }))}
          />
        </label>
        <div className={styles.grille}>
          {(['du', 'au'] as const).map((cle) => (
            <label key={cle} className={styles.champ}>
              {t(`regie:priseRendezVous.${cle}`)}
              <Input
                type="datetime-local"
                required
                value={cle === 'du' ? du : au}
                onChange={(e) => {
                  if (cle === 'du') setDu(e.target.value);
                  else setAu(e.target.value);
                }}
              />
            </label>
          ))}
        </div>
        <label className={styles.champ}>
          {t('regie:priseRendezVous.note')}
          <Textarea maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p>{t('regie:priseRendezVous.typesException')}</p>
        {types
          .filter((v) => !v.archiveLe || typeIds.includes(v.id))
          .map((type) => (
            <Checkbox
              key={type.id}
              label={type.titre[langue] || type.titre.fr}
              checked={typeIds.includes(type.id)}
              onCheckedChange={(v) =>
                setTypeIds(
                  v === true ? [...typeIds, type.id] : typeIds.filter((id) => id !== type.id),
                )
              }
            />
          ))}
      </fieldset>
      {invalide ? <p role="alert">{t('regie:priseRendezVous.exceptionInvalide')}</p> : null}
      <div className={styles.actions}>
        <Button type="submit" loading={pending}>
          {t('commun:actions.enregistrer')}
        </Button>
        <Button variant="ghost" onClick={onFermer} disabled={pending}>
          {t('commun:actions.annuler')}
        </Button>
      </div>
    </form>
  );
}
